import { useState } from 'react'
import { useParams } from 'react-router-dom'
import type { WorkflowRunDetail, WorkflowRunPhase, WorkflowRunBranch, WorkflowRunApprovalRequest } from '@/types'
import { getPhaseEffectiveStatus } from '@/types'
import { Badge } from '@/components/ui'
import { WorkflowStatusBadge } from '../shared'
import { NodeCard } from './NodeCard'
import { Lock, ChevronDown, ChevronRight, GitBranch, Layers, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatTimestampShort, formatElapsedTime } from '@/lib/timeFormatters'
import { ApprovalBanner } from './ApprovalBanner'
import { ApprovalGateBadge } from './ApprovalGateBadge'
import { ApprovalHistorySection } from './ApprovalHistorySection'
import { SecurityReviewFindingsSection } from './SecurityReviewFindingsSection'
import { useAuth, useSecurityReviewApprovalBlock } from '@/hooks'
import { WorkflowErrorCodes } from '@/lib/workflowErrorCodes'

interface Props {
  run: WorkflowRunDetail
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
  canApprove?: boolean
  onApprove?: (phaseId: string, comment?: string) => void
  onReject?: (phaseId: string, reason: string) => void
  isApproving?: boolean
  isRejecting?: boolean
}

const phaseStatusBorderMap: Record<string, string> = {
  Completed: '!border-success-foreground',
  Failed: '!border-danger-foreground',
  Running: '!border-info-foreground',
  Pending: '!border-neutral-foreground',
  Skipped: '!border-neutral-foreground',
  Cancelled: '!border-cancelled-foreground',
  AwaitingApproval: '!border-warning-foreground',
  AwaitingUserInput: '!border-warning-foreground',
}

function getPhaseStatusBorder(status: string): string {
  return phaseStatusBorderMap[status] || 'border-gray-300'
}

function PhaseHeader({ phase, isExpanded, onToggle, hasTokenExhausted, approvalRequest }: {
  phase: WorkflowRunPhase
  isExpanded: boolean
  onToggle: () => void
  hasTokenExhausted?: boolean
  approvalRequest?: WorkflowRunApprovalRequest
}) {
  const Chevron = isExpanded ? ChevronDown : ChevronRight
  const duration = formatElapsedTime(phase.startedAt, phase.completedAt)
  const effectiveStatus = getPhaseEffectiveStatus(phase)
  const gateLabel = approvalRequest?.status === 'Approved'
    ? 'Approved Gate'
    : approvalRequest?.status === 'Rejected'
      ? 'Rejected Gate'
      : 'Pending Gate'
  const gateClassName = approvalRequest?.status === 'Approved'
    ? 'text-xs gap-1 border-success-foreground text-success-foreground'
    : approvalRequest?.status === 'Rejected'
      ? 'text-xs gap-1 border-danger-foreground text-danger-foreground'
      : 'text-xs gap-1'

  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 px-4 py-3 w-full text-left transition-colors hover:bg-muted/50"
    >
      <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
      <Layers className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="font-semibold">{phase.name}</span>
      {duration && (
        <span className="text-sm text-muted-foreground">{duration}</span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {hasTokenExhausted && (
          <Badge variant="outline" className="text-xs gap-1 border-warning-foreground text-warning-foreground">
            <AlertTriangle className="h-3 w-3" /> Token limit
          </Badge>
        )}
        {phase.requiresApproval && (
          <Badge variant="outline" className={gateClassName}>
            {approvalRequest?.status !== 'Approved' && <Lock className="h-3 w-3" />}{gateLabel}
          </Badge>
        )}
        <WorkflowStatusBadge status={effectiveStatus} />
      </div>
    </button>
  )
}

function BranchSection({ branch, isExpanded, onToggle, selectedNodeId, onSelectNode }: {
  branch: WorkflowRunBranch
  isExpanded: boolean
  onToggle: () => void
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-2 hover:text-foreground transition-colors"
      >
        {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <GitBranch className="h-3 w-3" />
        {branch.name} {branch.isParallel ? '(parallel)' : '(sequential)'}
        <span className="ml-1 font-normal">&middot; {branch.nodes.length} nodes</span>
      </button>
      {isExpanded && (
        <div className="space-y-2 ml-3">
          {branch.nodes.map((node) => (
            <NodeCard
              key={node.id}
              node={node}
              isSelected={selectedNodeId === node.id}
              onClick={() => onSelectNode(node.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function isUserRoleAuthorized(approvalRoles: string[] | null | undefined, userProjectRoles: string[], isAdmin: boolean): boolean {
  if (!approvalRoles || approvalRoles.length === 0) return true
  if (isAdmin) return true
  return userProjectRoles.some(role => approvalRoles.includes(role))
}

export function PipelineDiagram({ run, selectedNodeId, onSelectNode, canApprove, onApprove, onReject, isApproving, isRejecting }: Props) {
  const { projectId } = useParams()
  const { getProjectRoles, isAdmin } = useAuth(projectId)
  const userProjectRoles = projectId ? getProjectRoles(projectId) : []

  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(() => {
    const set = new Set<string>()
    for (const p of run.phases) {
      if (p.status !== 'Skipped') set.add(p.id)
    }
    return set
  })

  const [expandedBranches, setExpandedBranches] = useState<Set<string>>(() => {
    const set = new Set<string>()
    for (const p of run.phases) {
      for (const b of p.branches) set.add(b.id)
    }
    return set
  })

  const hasSecurityReviewFindingsNode = run.phases.some(p =>
    p.branches.some(b => b.nodes.some(n => n.taskType === 'fetch-security-findings'))
  )

  const securityReviewBlock = useSecurityReviewApprovalBlock(run.projectId, hasSecurityReviewFindingsNode ? run.id : null)

  const togglePhase = (id: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleBranch = (id: string) => {
    setExpandedBranches(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-3">
      {run.phases.map((phase) => {
        const effectiveStatus = getPhaseEffectiveStatus(phase)
        const isPhaseExpanded = expandedPhases.has(phase.id)
        const approvalRequest = phase.requiresApproval
          ? run.approvalRequests.find(a => a.phaseId === phase.id)
          : undefined
        const phaseApprovalRequests = phase.requiresApproval
          ? run.approvalRequests.filter(a => a.phaseId === phase.id)
          : []
        const pendingApprovalRequest = phase.requiresApproval && phase.status === 'AwaitingApproval'
          ? (approvalRequest ?? {
              id: `pending-${phase.id}`,
              runId: run.id,
              phaseId: phase.id,
              phaseName: phase.name,
              message: phase.approvalMessage ?? '',
              status: 'Pending' as const,
              decidedBy: null,
              decidedAt: null,
              comment: null,
              approvalRoles: phase.approvalRoles ?? null,
            })
          : undefined
        const phaseHasTokenExhausted = phase.branches.some(b =>
          b.nodes.some(n => n.errorCode === WorkflowErrorCodes.TokenExhausted)
        )

        return (
          <div key={phase.id}>
            {phase.requiresApproval && pendingApprovalRequest && hasSecurityReviewFindingsNode && (
              <div className="pb-3">
                <SecurityReviewFindingsSection
                  projectId={run.projectId}
                  workflowRunId={run.id}
                  canDecide={Boolean(canApprove) && isUserRoleAuthorized(pendingApprovalRequest.approvalRoles, userProjectRoles, isAdmin)}
                />
              </div>
            )}
            {phase.requiresApproval && pendingApprovalRequest && onApprove && onReject && (
              <div className="pb-3">
                <ApprovalBanner
                  approvalRequest={pendingApprovalRequest}
                  canApprove={canApprove ?? false}
                  isRoleAuthorized={isUserRoleAuthorized(pendingApprovalRequest.approvalRoles, userProjectRoles, isAdmin)}
                  onApprove={onApprove}
                  onReject={onReject}
                  isApproving={isApproving}
                  isRejecting={isRejecting}
                  approveDisabled={securityReviewBlock.disabled}
                  approveDisabledReason={securityReviewBlock.reason}
                />
              </div>
            )}
            {phase.requiresApproval && approvalRequest && approvalRequest.status !== 'Pending' && (
              <ApprovalGateBadge
                approvalRequest={approvalRequest}
                status={phase.status as any}
              />
            )}
            {phase.requiresApproval && !pendingApprovalRequest && !approvalRequest && phase.status === 'AwaitingApproval' && (
              <ApprovalGateBadge
                status={phase.status as any}
              />
            )}
            {phaseApprovalRequests.length > 0 && (
              <div className="mb-3">
                <ApprovalHistorySection approvalRequests={phaseApprovalRequests} />
              </div>
            )}
            <div className={cn('border-2 rounded-lg overflow-hidden bg-card', getPhaseStatusBorder(effectiveStatus))}>
              <PhaseHeader
                phase={phase}
                isExpanded={isPhaseExpanded}
                onToggle={() => togglePhase(phase.id)}
                hasTokenExhausted={phaseHasTokenExhausted}
                approvalRequest={approvalRequest}
              />
              {isPhaseExpanded && (
                <div className="p-4">
                  <div className="flex gap-4 text-[11px] text-muted-foreground mb-3 pb-2 border-b">
                    <span>Started: {formatTimestampShort(phase.startedAt)}</span>
                    <span>Completed: {formatTimestampShort(phase.completedAt)}</span>
                  </div>
                  {phase.branches.map((branch, bi) => (
                    <div key={branch.id} className={cn(bi > 0 && 'mt-3 pt-3 border-t')}>
                      {phase.branches.length > 1 ? (
                        <BranchSection
                          branch={branch}
                          isExpanded={expandedBranches.has(branch.id)}
                          onToggle={() => toggleBranch(branch.id)}
                          selectedNodeId={selectedNodeId}
                          onSelectNode={onSelectNode}
                        />
                      ) : (
                        <div className="space-y-2">
                          {branch.nodes.map((node) => (
                            <NodeCard
                              key={node.id}
                              node={node}
                              isSelected={selectedNodeId === node.id}
                              onClick={() => onSelectNode(node.id)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
